<template>
  <div data-testid="materiality-filters-header" class="d-flex flex-column gap-3">
    <div class="d-flex align-center gap-2" data-testid="materiality-filters-header__controls" v-if="!isViewMode">
      <v-menu
        v-model="statementMenuOpen"
        left
        offset-y
        transition="slide-y-transition"
        data-testid="materiality-filters-header__statement-menu"
      >
        <template v-slot:activator="{ on, attrs }">
          <div
            class="statement-selector__activator cursor-pointer"
            v-bind="attrs"
            v-on="on"
            data-testid="materiality-filters-header__statement-selector"
          >
            <div class="statement-chip__content">
              <span class="statement-chip__label font-weight-semibold">{{ statementSelectorLabel }}</span>
            </div>
            <img class="statement-selector__chevron" src="/img/icons/downArrow.svg" alt="dropdown icon" />
          </div>
        </template>
        <v-card class="statement-selector__menu">
          <v-list class="pa-0 d-flex flex-column gap-1" data-testid="materiality-filters-header__statement-options-list">
            <v-list-item
              v-for="option in statementOptions"
              :key="option.name"
              @click="handleStatementSelect(option.name)"
              class="statement-selector__item"
              :class="{ 'statement-selector__item--active': option.name === internalReportingType }"
              data-testid="materiality-filters-header__statement-option"
            >
              <div class="statement-chip__content">
                <span class="statement-chip__label">{{ option.labelName }}</span>
              </div>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
      <lb-selectmultiplemonths
        data-testid="materiality-filters-header__period-selector"
        type="month"
        hidedetails
        v-model="internalPeriods"
        :label="'Applicable ' + $nova.labelsName('materiality_page', 'month_label', 'Period')"
        id="applicable-periods"
        :class="periodSelectorClass"
        :min="minDate"
        :max="maxDate"
        :value="internalPeriods || []"
        :key="new Date().getTime()"
        roundedClass="rounded-lg"
        appendicon="mdi-calendar-range"
        prependinnericon=""
        :style="periodSelectorStyle"
        :multiple="isViewMode ? false : true"
        close-text="Cancel"
        ok-text="Proceed"
      />
      <v-spacer></v-spacer>
      
      <!-- Optional slot for additional controls (e.g., toggle switch) -->
      <slot name="additional-controls"></slot>
    </div>
    <div v-else>
      <div class="d-flex align-center gap-2" data-testid="materiality-filters-header__controls">
        <v-menu
      v-model="viewOptionsMenuOpen"
      offset-y
      left
      transition="slide-y-transition"
      :close-on-content-click="false"
      data-testid="gl-action-toolbar__view-options-menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <div
          v-bind="attrs"
          v-on="on"
          class="view-options-chip cursor-pointer"
          data-testid="gl-action-toolbar__view-options-btn"
        >
          <!-- <v-icon small class="view-options-chip__icon">mdi-eye-outline</v-icon> -->
          <span class="view-options-chip__label">{{ viewOptionsLabel }}</span>
          <v-icon small class="view-options-chip__chevron">mdi-chevron-down</v-icon>
        </div>
      </template>
      <v-card class="view-options-card" data-testid="gl-action-toolbar__view-options-card">
        <!-- View Toggle Section -->
        <div class="view-options-section d-flex flex-column gap-3">
          <div class="view-options-section__header">Cycle Type</div>
          <div class="view-options-section__content d-flex flex-column gap-3">
            <v-radio-group
              v-model="internalIsCycleType"
              dense
              hide-details
              class="ma-0 pa-0"
            >
              <v-radio
                value="actual"
                color="primary"
                class="view-options-radio ma-0 pa-0"
                data-testid="gl-action-toolbar__list-view-radio"
              >
                <template v-slot:label>
                  <span class="view-options-radio__label">{{ cycleTypeLabels.actual }} Cycle</span>
                </template>
              </v-radio>
              <v-radio
                value="forecast"
                color="primary"
                class="view-options-radio ma-0 pa-0"
                data-testid="gl-action-toolbar__commentary-view-radio"
              >
                <template v-slot:label>
                  <span class="view-options-radio__label">{{ cycleTypeLabels.forecast }} Cycle</span>
                </template>
              </v-radio>
            </v-radio-group>
          </div>
        </div>

        <v-divider class="my-2"></v-divider>
        <!-- Read Toggle Section - "All" option available for both views -->
        <div class="view-options-section d-flex flex-column gap-3">
          <div class="view-options-section__header">Modules</div>
          <div class="view-options-section__content d-flex flex-column gap-3">
            <v-radio-group
              v-model="internalCycletypeReadview"
              dense
              hide-details
              class="ma-0 pa-0"
            >
              <!-- All option - Available for both List View and Commentary View -->
              <v-radio
                value="pl"
                color="primary"
                class="view-options-radio ma-0 pa-0"
                data-testid="gl-action-toolbar__all-radio"
              >
                <template v-slot:label>
                  <span class="view-options-radio__label">{{ statementTabs[1].label }}</span>
                </template>
              </v-radio>
              <v-radio
                value="bs"
                color="primary"
                class="view-options-radio ma-0 pa-0"
                data-testid="gl-action-toolbar__vs-proforma-radio"
              >
                <template v-slot:label>
                  <span class="view-options-radio__label">{{ statementTabs[0].label }}</span>
                </template>
              </v-radio>
            </v-radio-group>
          </div>
        </div>
      </v-card>
    </v-menu>

     <lb-selectmultiplemonths
        data-testid="materiality-filters-header__period-selector"
        type="month"
        hidedetails
        v-model="internalViewPeriods"
        :label="'Applicable ' + $nova.labelsName('materiality_page', 'month_label', 'Period')"
        id="applicable-periods"
        :class="periodSelectorClass"
        :min="minDate"
        :max="maxDate"
        :value="internalViewPeriods || []"
        :key="new Date().getTime()"
        roundedClass="rounded-lg"
        appendicon="mdi-calendar-range"
        prependinnericon=""
        :style="periodSelectorStyle"
        :multiple="isViewMode ? false : true"
        close-text="Cancel"
        ok-text="Proceed"
      />

      <base-button
        data-testid="materiality-filters-header__get-btn"
        @click="handleGetPeriods"
        primary
        label="Get"
      />
    </div>

    </div>
    <!-- Selected Months Chips -->
    <div 
      v-if="!isViewMode"
      class="d-flex align-center gap-3"
      data-testid="materiality-filters-header__selected-months"
    >
      <h6 class="selectmonth">Selected Months</h6>
      <v-chip
        v-for="(period, index) in internalPeriods"
        :key="index"
        :data-testid="`materiality-filters-header__month-chip-${index}`"
        class="chip-gray-close"
        close
        label
        color="#F3F6FE"
        text-color="#4285F4"
        @click:close="handleRemoveMonth(period)"
        close-icon="mdi-close"
      >
        {{ formatPeriod(period) }}
      </v-chip>
    </div>
    <div v-else class="d-flex align-center gap-3"> 
      <h6 class="selectmonth" v-if="normalizedViewPeriods.length > 0">Selected Months</h6>
      <v-chip
        v-for="(period, index) in normalizedViewPeriods"
        :key="index"
        :data-testid="`materiality-filters-header__month-chip-${index}`"
        class="chip-gray-close"
        close
        label
        color="#F3F6FE"
        text-color="#4285F4"
        @click:close="handleRemoveMonth(period)"
        close-icon="mdi-close"
      >
        {{ formatPeriod(period) }}
      </v-chip>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { CYCLE_TYPE_LABELS, STATEMENT_TABS } from '../../utils/constants';

export default {
  name: 'MaterialityFiltersHeader',

  props: {
    // Statement/Reporting type
    reportingType: {
      type: String,
      default: 'pl'
    },

    // Statement options for dropdown
    statementOptions: {
      type: Array,
      required: true
    },

    // Selected periods/months
    periods: {
      type: Array,
      default: () => []
    },

    // Min date for period selector
    minDate: {
      type: String,
      default: ''
    },

    // Max date for period selector
    maxDate: {
      type: String,
      default: ''
    },

    // Custom class for period selector
    periodSelectorClass: {
      type: String,
      default: 'ml-2'
    },

    // Custom style for period selector
    periodSelectorStyle: {
      type: String,
      default: 'width: 250px'
    },

    // // Custom class for chip container
    // chipContainerClass: {
    //   type: String,
    //   default: ''
    // },

    // Control when to show selected months
    showSelectedMonths: {
      type: Boolean,
      default: true
    },

    // Additional condition for showing chips (for RU component)
    additionalShowCondition: {
      type: Boolean,
      default: true
    },

    //View Mode Prop
    isViewMode: {
      type: Boolean,
      default: false
    },

    // View options props (for view mode)
    isCommentaryView: {
      type: String,
      default: 'actual'
    },

    cycletypeReadview: {
      type: String,
      default: 'pl'
    }
  },

  data() {
    return {
      viewOptionsMenuOpen: false,
      statementMenuOpen: false,
      cycleTypeLabels: CYCLE_TYPE_LABELS,
      statementTabs: STATEMENT_TABS,
      localIsCommentaryView: 'actual',
      localCycletypeReadview: 'pl',
    };
  },

  mounted() {
    
    // Initialize local state from props
    // this.localIsCommentaryView = this.isCommentaryView;
    // this.localCycletypeReadview = this.cycletypeReadview;
    
    // // Initialize internalViewPeriods from periods prop if store value is empty
    // if (!this.materialityViewPeriods || this.materialityViewPeriods.length === 0) {
    //   if (this.periods && this.periods.length > 0) {
    //     this.SET_MATERIALITY_VIEW_PERIODS(this.periods);
    //   } else {
    //     // Ensure it's an empty array rather than null/undefined
    //     this.SET_MATERIALITY_VIEW_PERIODS([]);
    //   }
    // }
  },

  watch: {
    isCommentaryView: {
      handler(newVal) {
        this.localIsCommentaryView = newVal;
      },
      immediate: true
    },
    cycletypeReadview: {
      handler(newVal) {
        this.localCycletypeReadview = newVal;
      },
      immediate: true
    }
  },

  computed: {
    ...mapGetters('varianceanalysis', ['materialityViewPeriods']),

    normalizedViewPeriods() {
      if (Array.isArray(this.internalViewPeriods)) {
        return this.internalViewPeriods;
      }
      return this.internalViewPeriods ? [this.internalViewPeriods] : [];
    },

    /**
     * Internal isCommentaryView with two-way binding
     */
    internalIsCycleType: {
      get() {
        return this.localIsCommentaryView;
      },
      set(value) {
        this.localIsCommentaryView = value;
        this.$emit('update:isCommentaryView', value);
      }
    },
    

    /**
     * Internal cycletypeReadview with two-way binding
     */
    internalCycletypeReadview: {
      get() {
        return this.localCycletypeReadview;
      },
      set(value) {
        this.localCycletypeReadview = value;
        this.$emit('update:cycletypeReadview', value);
      }
    },

    /**
     * Internal viewPeriods with two-way binding using Vuex
     */
    internalViewPeriods: {
      get() {
        return this.materialityViewPeriods;
      },
      set(value) {
        this.SET_MATERIALITY_VIEW_PERIODS(value);
      }
    },
    /**
     * Label for statement selector - shows currently selected statement
     */
    statementSelectorLabel() {
      const selectedOption = this.statementOptions.find(opt => opt.name === this.internalReportingType);
      return selectedOption ? selectedOption.labelName : 'Modules';
    },

    /**
     * Label for view options button - shows current selections
     */
    viewOptionsLabel() {
      const viewType = this.localIsCommentaryView === 'forecast' ? this.cycleTypeLabels.forecast : this.cycleTypeLabels.actual;
      let moduleType = '';
      
      if (this.localCycletypeReadview === 'pl') {
        moduleType = this.statementTabs[1].label; // Profit & Loss
      } else if (this.localCycletypeReadview === 'bs') {
        moduleType = this.statementTabs[0].label; // Balance Sheet
      }
      
      return `${viewType} Cycle - ${moduleType}`;
    },
    /**
     * Internal reporting type with two-way binding
     */
    internalReportingType: {
      get() {
        return this.reportingType;
      },
      set(value) {
        this.$emit('update:reportingType', value);
      }
    },

    /**
     * Internal periods with two-way binding
     */
    internalPeriods: {
      get() {
        return this.periods;
      },
      set(value) {
        this.$emit('update:periods', value);
      }
    },
  },

  methods: {
    ...mapMutations('varianceanalysis', ['SET_MATERIALITY_VIEW_PERIODS']),

    /**
     * Handle statement selection from dropdown
     * @param {string} value - Selected statement value
     */
    handleStatementSelect(value) {
      this.internalReportingType = value;
      this.statementMenuOpen = false;
    },

    handleGetPeriods() {
      let periodsArray = this.internalViewPeriods;
      
      // Validate periods
      if (!periodsArray) {
        return;
      }
      
      // Convert string to array if needed (single selection mode)
      if (typeof periodsArray === 'string') {
        periodsArray = [periodsArray];
      }
      
      // Validate array
      if (!Array.isArray(periodsArray) || periodsArray.length === 0) {
        return;
      }
      
      const payload = {
        periods: periodsArray,
        cycleType: this.internalIsCycleType,
        reportingType: this.internalCycletypeReadview
      };
      
      this.$emit('periods-changed', payload);
    },
    /**
     * Format period from YYYY-MM to MM-YYYY format
     * @param {string} period - Period in YYYY-MM format
     * @returns {string} Period in MM-YYYY format
     */
    formatPeriod(period) {
      if (!period) return '';
      const [year, month] = period.split('-');
      return `${month}-${year}`;
    },

    /**
     * Handle removal of a month chip
     * @param {string} period - The period to remove
     */
    handleRemoveMonth(period) {
      if (this.isViewMode) {
        let newPeriods = [];
        if (Array.isArray(this.internalViewPeriods)) {
          newPeriods = this.internalViewPeriods.filter(p => p !== period);
        } else if (this.internalViewPeriods === period) {
          newPeriods = [];
        }
        
        // Update local and emit
        this.internalViewPeriods = newPeriods;
        // If single selection mode, we might need to handle empty array -> null/empty string conversion if needed
        // But for now updating to empty array is safe for the prop
      } else {
        this.$emit('removeMonth', period);
      }
    },


  }
};
</script>

<style scoped>
/* Component-specific styles if needed */
.chip-gray-close >>> .v-chip__close {
  color: var(--grey-500) !important;
}
.selectmonth {
  color: var(--grey-500);
}

/* Statement Selector Dropdown Styles */
.statement-selector__activator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: var(--grey-50);
  border: 1px solid var(--grey-300);
  border-radius: 8px;
  min-width: 250px;
  transition: all 0.2s ease;
}

.statement-selector__activator:hover {
  background-color: var(--grey-100);
  border-color: var(--grey-400);
}

.statement-chip__content {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
}

.statement-chip__label {
  font-size: 14px;
  color: var(--grey-700);
  white-space: nowrap;
}

.statement-selector__chevron {
  color: var(--grey-500);
  margin-left: auto;
}

.statement-selector__menu {
  min-width: 250px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.statement-selector__item {
  cursor: pointer;
  padding: 8px 12px;
  transition: background-color 0.2s ease;
}

.statement-selector__item:hover {
  background-color: var(--grey-50);
}

.statement-selector__item--active {
  background-color: var(--primary-50);
}

.statement-selector__item--active .statement-chip__label {
  color: var(--primary-600);
  font-weight: 600;
}

/* View Options Dropdown Styles */
.view-options-chip {
  display: flex;
  align-items: center;
  height: 40px;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid var(--Text-Grey-Weak, #B2B2B4);
  background: var(--background-gray-weak, #FFFFFF);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.view-options-chip:hover {
  border-color: var(--color-primary, #1976d2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.view-options-chip__icon {
  color: var(--Text-Grey-Default, #2C2C35);
  margin-top: 2px;
  flex-shrink: 0;
}

.view-options-chip__label {
  color: var(--Text-Grey-Default, #2C2C35);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}

.view-options-chip__chevron {
  color: var(--color-status-card-text, #828285);
  flex-shrink: 0;
}

.view-options-card {
  min-width: 200px;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 4px 4px 20px 0 rgba(0, 0, 0, 0.16);
  border: 1px solid var(--Text-Grey-Weak, #B2B2B4);
}

.view-options-section__header {
  font-size: 14px;
  font-weight: 600;
  color: var(--grey-900, #2C2C35);
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.view-options-section__content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
::v-deep .view-options-section__content .v-input--radio-group__input{
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.view-options-radio {
  margin: 0 !important;
}

.view-options-radio >>> .v-input--selection-controls__input {
  margin-right: 8px;
}

.view-options-radio >>> .v-radio {
  margin-bottom: 0;
}

.view-options-radio__label {
  font-size: 14px;
  font-weight: 400;
  color: var(--grey-500, #828285);
  line-height: 20px;
}
</style>
